import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { ResponsiveBar, BarCustomLayerProps } from '@nivo/bar';
import { DeepAnalysisUpsellSummary, DeepAnalysisUpsellValuesByTag } from '../../../../../shared/api/controllers/engagementAnalyticsReporting';
import { barChartDefaults, nivoChartTheme } from '../../../../../shared/constants/chartTheme';
import { breakpoint_large, breakpoint_macbook_small, breakpoint_macbook } from '../../../../../shared/constants/breakpoints';
import { MetricInfoTooltip } from './MetricInfoTooltip';
import { ChartTooltip } from './ChartTooltip';
import { buildTopUpsellItems, UPSELL_ITEM_COLORS as ITEM_COLORS } from './UpsellsOverviewChart';
import { UPSELL_DAYS, getUpsellDaysInRange } from './upsellTiming';
import { UpsellLocationRate } from './upsellLocationRates';
import { parseAnalyticsDate } from '../../../../../shared/utils/engagementAnalyticsTransformers';
import { Checkbox as AnalyticsFilterCheckbox } from './AssistantAnalyticsFilter';
import { voicify_spark_darker, voicify_spark_darkest, voicify_spark_lightest, chart_blue_2, color_text_link, color_variants_ocean_light_1, voicify_spark_medium, color_text_dashboard_secondary, color_shades_darker, color_shades_darkest, color_shades_light } from '../../../../../shared/constants/colors';
import { CallDataTable, CallDataTh, CallDataRow, CallDataTd, SortIcon, BaseTile, TileTitle, BigNumber, NumberSubtext, ViewDetailsButton, FilterIconSvg, PhoneFilterInput, ClearFilterLink, CallTag, PhoneFilterTagRemove, ClearAllFiltersButton, TileHeader, TileHeaderStatsSection, TileHeaderNumber, TileHeaderLabel } from './ApplicationDashboard.styles';

export interface UpsellSummaryEntry {
    applicationId: string;
    summary: DeepAnalysisUpsellSummary;
}

interface UpsellAnalyticsDashboardProps {
    summaries: UpsellSummaryEntry[];
    successCountByItem: Record<string, number>;
    hiddenItemKeys: Set<string>;
    upsellAttempts: number;
    upsellAttemptsByDay?: Record<string, number>;
    upsellAttemptsByHour?: Record<string, number>;
    locationSuccessRates?: UpsellLocationRate[];
    selectedLocationCount: number;
    valueConfigByEntity: Record<string, DeepAnalysisUpsellValuesByTag>;
    menuPricesByItem?: Record<string, number>;
    savedOverrideKeys?: Set<string>;
    valueScopeKey?: string;
    onSaveItemValue: (tag: string, dollarValue: number) => Promise<void>;
    onSaveMenuPrices: (prices: Record<string, number>) => Promise<void>;
    isLoading: boolean;
    startDate: string;
    endDate: string;
    appNameMap?: Record<string, string>;
    sessionLanguages?: Record<string, 'english' | 'spanish'>;
    spanishSessionKeys?: Set<string>;
    showLanguageBreakdown?: boolean;
    onBack: () => void;
}

type SortMode = 'count-desc' | 'count-asc' | 'revenue-desc' | 'revenue-asc' | 'item-asc' | 'item-desc' | 'estimate-asc' | 'estimate-desc' | 'type-estimate' | 'type-menu';
type TemporalMode = 'days' | 'hours';

interface UpsellItem {
    key: string;
    name: string;
    count: number;
    revenue: number;
}

interface UpsellSession {
    applicationId: string;
    sessionId: string;
    callTime: string;
}

const formatRate = (rate: number) => `${Number(rate.toFixed(1)).toLocaleString()}%`;
const LocationRateLabels: React.FC<BarCustomLayerProps<Record<string, string | number>>> = ({ bars }) => (
    <g aria-label="Location success rates" pointerEvents="none">
        {bars.map(bar => <text key={bar.key} x={bar.x + bar.width / 2} y={bar.y - 10}
            textAnchor="middle" style={{ fontSize: 11, fill: color_shades_darkest, fontWeight: 600 }}>
            {formatRate(Number(bar.data.value))}
        </text>)}
    </g>
);

const TimingItemLabels: React.FC<BarCustomLayerProps<Record<string, string | number>>> = ({ bars, innerHeight }) => (
    <g aria-label="Item names" pointerEvents="none">
        {bars.filter(bar => Number(bar.data.value) > 0).map(bar => {
            const name = String(bar.data.id);
            return <g key={bar.key} transform={`translate(${bar.x + bar.width / 2},${innerHeight + 12})`}>
                <text textAnchor="end" dominantBaseline="middle" transform="rotate(-45)"
                    style={{ fontSize: 10, fill: chart_blue_2, fontWeight: 500 }}>
                    <title>{name}</title>{name.length > 18 ? `${name.slice(0, 17)}…` : name}
                </text>
            </g>;
        })}
    </g>
);
const countTicks = (maximum: number): number[] => {
    const roughStep = Math.max(1, maximum / 4);
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const step = [1, 2, 5, 10].map(value => value * magnitude).find(value => value >= roughStep) || magnitude * 10;
    return Array.from({ length: Math.max(1, Math.ceil(maximum / step)) + 1 }, (_, index) => index * step);
};

const PAGE_SIZE = 20;
const ValueEditor = styled.div`align-items: center; display: flex; gap: 6px; justify-content: flex-end; min-width: 160px;`;
const CurrencyInput = styled.input`border: 1px solid #cbd5e1; border-radius: 6px; color: #334155; font: inherit; font-size: 12px; min-width: 0; padding: 6px 7px; width: 88px; &:focus { border-color: #0284c7; outline: 2px solid rgba(2, 132, 199, .12); } &:disabled { background: #f8fafc; cursor: wait; }`;
const SaveValueButton = styled.button`background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px; color: #0369a1; cursor: pointer; font: inherit; font-size: 11px; font-weight: 600; padding: 6px 8px; white-space: nowrap; &:hover { background: #dbeafe; } &:disabled { cursor: wait; opacity: .6; }`;
const CancelValueButton = styled(SaveValueButton)`background: #fff; border-color: ${color_shades_light}; color: ${color_shades_darker}; &:hover { background: #f4f4f6; }`;
const ValueAmount = styled.span`font-variant-numeric: tabular-nums; white-space: nowrap;`;
const ValueNote = styled.span`color: ${color_shades_darker}; font-size: 10px;`;
const ValueTypeBadge = styled.span<{ $menu: boolean }>`background: ${props => props.$menu ? voicify_spark_lightest : '#f4f4f6'}; border-radius: 4px; color: ${props => props.$menu ? voicify_spark_darkest : color_shades_darker}; display: inline-block; font-size: 11px; padding: 3px 6px; white-space: nowrap;`;
const SaveError = styled.div`color: #b91c1c; font-size: 10px; margin-top: 4px; max-width: 180px; text-align: right;`;

const normalize = (value: string) => value.toLocaleLowerCase();
const formatCurrency = (value: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value || 0);
const truncateChartLabel = (value: string) => value.length > 15 ? `${value.slice(0, 15)}…` : value;
const formatItemName = (tag: string) => tag
    .replace(/-/g, ' ')
    .split(' ')
    .filter(word => word && word.toLocaleLowerCase() !== 'upsold')
    .map(word => `${word.charAt(0).toLocaleUpperCase()}${word.slice(1).toLocaleLowerCase()}`)
    .join(' ');

const UpsellAnalyticsDashboard: React.FC<UpsellAnalyticsDashboardProps> = ({
    summaries,
    successCountByItem,
    hiddenItemKeys,
    upsellAttempts,
    upsellAttemptsByDay = {},
    upsellAttemptsByHour = {},
    locationSuccessRates = [],
    selectedLocationCount,
    valueConfigByEntity,
    menuPricesByItem = {},
    savedOverrideKeys = new Set(),
    valueScopeKey = '',
    onSaveItemValue,
    onSaveMenuPrices,
    isLoading,
    startDate,
    endDate,
    appNameMap,
    sessionLanguages,
    spanishSessionKeys,
    showLanguageBreakdown = false,
    onBack
}) => {
    const [sortMode, setSortMode] = useState<SortMode>('count-desc');
    const [temporalMode, setTemporalMode] = useState<TemporalMode>('days');
    const [temporalMetric, setTemporalMetric] = useState<'counts' | 'rates'>('counts');
    const timingScrollRef = useRef<HTMLDivElement>(null);
    const [hoveredLocationId, setHoveredLocationId] = useState<string | null>(null);
    const [selectedItem, setSelectedItem] = useState<string | null>(null);
    const [selectedItemTag, setSelectedItemTag] = useState<string | null>(null);
    const [excludedItemKeys, setExcludedItemKeys] = useState<Set<string>>(new Set());
    const [draftIncludedItemKeys, setDraftIncludedItemKeys] = useState<Set<string>>(new Set());
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterSearch, setFilterSearch] = useState('');
    const [oldestFirst, setOldestFirst] = useState(false);
    const [page, setPage] = useState(0);
    const [valueDrafts, setValueDrafts] = useState<Record<string, string>>({});
    const [manualSavedState, setManualSavedState] = useState<{ scopeKey: string; values: Record<string, number> }>({ scopeKey: valueScopeKey, values: {} });
    const manualSavedValues = manualSavedState.scopeKey === valueScopeKey ? manualSavedState.values : {};
    const [editingValueTag, setEditingValueTag] = useState<string | null>(null);
    const [savingValueTag, setSavingValueTag] = useState<string | null>(null);
    const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
    const [menuSaveState, setMenuSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [menuSaveError, setMenuSaveError] = useState('');
    const [menuSaveRetry, setMenuSaveRetry] = useState(0);
    const menuSaveAttempts = useRef(new Map<string, Promise<void>>());
    const saveMenuPricesRef = useRef(onSaveMenuPrices);
    saveMenuPricesRef.current = onSaveMenuPrices;
    const autoSavePrices = Object.fromEntries(Object.entries(menuPricesByItem)
        .filter(([tag]) => !Object.prototype.hasOwnProperty.call(manualSavedValues, tag)));
    const menuSaveKey = JSON.stringify([valueScopeKey, Object.entries(autoSavePrices).sort(([a], [b]) => a.localeCompare(b))]);

    useEffect(() => {
        setValueDrafts({});
        setEditingValueTag(null);
        setManualSavedState({ scopeKey: valueScopeKey, values: {} });
        setSaveErrors({});
        setMenuSaveState('idle');
        setMenuSaveError('');
    }, [valueScopeKey]);

    useEffect(() => {
        if (isLoading || savingValueTag !== null || !Object.keys(autoSavePrices).length) return;
        let active = true;
        const attemptKey = `${menuSaveKey}|${menuSaveRetry}`;
        let save = menuSaveAttempts.current.get(attemptKey);
        if (!save) {
            const saveMenuPrices = saveMenuPricesRef.current;
            save = Promise.resolve().then(() => saveMenuPrices(autoSavePrices));
            menuSaveAttempts.current.set(attemptKey, save);
        }
        setMenuSaveState('saving');
        setMenuSaveError('');
        save.then(() => {
            if (active) setMenuSaveState('saved');
        }).catch(error => {
            if (active) {
                setMenuSaveState('error');
                setMenuSaveError(error instanceof Error ? error.message : 'Could not automatically save menu values.');
            }
        });
        return () => { active = false; };
    }, [menuSaveKey, isLoading, savingValueTag, menuSaveRetry]);

    const configuredValuesByItem = useMemo(() => {
        const valuesByItem = new Map<string, number[]>();
        Object.values(valueConfigByEntity).forEach(config => {
            Object.entries(config || {}).forEach(([tag, value]) => {
                const key = normalize(tag);
                const values = valuesByItem.get(key) || [];
                values.push(Number(value?.dollarValue) || 0);
                valuesByItem.set(key, values);
            });
        });
        return valuesByItem;
    }, [valueConfigByEntity]);

    const getEstimateInputValue = (itemKey: string): string => {
        if (Object.prototype.hasOwnProperty.call(valueDrafts, itemKey)) return valueDrafts[itemKey];
        if (manualSavedValues[itemKey] !== undefined) return String(manualSavedValues[itemKey]);
        if (!savedOverrideKeys.has(itemKey) && menuPricesByItem[itemKey] !== undefined) return String(menuPricesByItem[itemKey]);
        const values = Array.from(new Set(configuredValuesByItem.get(itemKey) || []));
        return values.length === 1 ? String(values[0]) : '';
    };

    const isMenuValue = (itemKey: string) => menuPricesByItem[itemKey] !== undefined
        && manualSavedValues[itemKey] === undefined && !savedOverrideKeys.has(itemKey);

    const items = useMemo(() => {
        const byName = new Map<string, UpsellItem>();
        summaries.forEach(({ summary }) => {
            (summary.tagsTotals || []).forEach(({ tag, count }) => {
                const key = normalize(tag);
                if (hiddenItemKeys.has(key)) return;
                const item = byName.get(key) || { key: tag, name: formatItemName(tag), count: 0, revenue: 0 };
                item.count += count || 0;
                byName.set(key, item);
            });
            (summary.upsellValues || []).forEach(value => {
                const name = value['upsold-item'];
                if (!name) return;
                const key = normalize(name);
                if (hiddenItemKeys.has(key)) return;
                const item = byName.get(key) || { key: name, name: formatItemName(name), count: 0, revenue: 0 };
                item.revenue += value['total-dollar-value'] || 0;
                byName.set(key, item);
            });
        });
        byName.forEach((item, key) => {
            if (successCountByItem[key] !== undefined) item.count = successCountByItem[key];
        });
        return Array.from(byName.values()).sort((a, b) => {
            if (sortMode.startsWith('type-')) {
                const difference = Number(isMenuValue(normalize(a.key))) - Number(isMenuValue(normalize(b.key)));
                return (sortMode === 'type-estimate' ? difference : -difference) || b.count - a.count || a.name.localeCompare(b.name);
            }
            if (sortMode === 'item-asc') return a.name.localeCompare(b.name);
            if (sortMode === 'item-desc') return b.name.localeCompare(a.name);
            if (sortMode.startsWith('estimate-')) {
                const aInput = getEstimateInputValue(normalize(a.key));
                const bInput = getEstimateInputValue(normalize(b.key));
                const aValue = aInput.trim() === '' ? null : Number(aInput);
                const bValue = bInput.trim() === '' ? null : Number(bInput);
                const aMissing = aValue === null || !Number.isFinite(aValue);
                const bMissing = bValue === null || !Number.isFinite(bValue);
                if (aMissing || bMissing) return Number(aMissing) - Number(bMissing) || a.name.localeCompare(b.name);
                return (sortMode === 'estimate-asc' ? aValue - bValue : bValue - aValue) || a.name.localeCompare(b.name);
            }
            if (sortMode === 'count-asc') return a.count - b.count || a.name.localeCompare(b.name);
            if (sortMode === 'revenue-desc') return b.revenue - a.revenue || b.count - a.count;
            if (sortMode === 'revenue-asc') return a.revenue - b.revenue || a.count - b.count;
            return b.count - a.count || a.name.localeCompare(b.name);
        });
    }, [summaries, sortMode, successCountByItem, hiddenItemKeys, valueDrafts, manualSavedValues, savedOverrideKeys, menuPricesByItem, configuredValuesByItem]);

    const visibleItems = useMemo(() => items.filter(item => !excludedItemKeys.has(normalize(item.key))), [items, excludedItemKeys]);
    const hasItemFilter = visibleItems.length < items.length;
    const filterItems = useMemo(() => items.filter(item => item.name.toLocaleLowerCase().includes(filterSearch.trim().toLocaleLowerCase())), [items, filterSearch]);
    useEffect(() => {
        if (editingValueTag && !visibleItems.some(item => normalize(item.key) === editingValueTag)) {
            setEditingValueTag(null);
            setValueDrafts(current => {
                const next = { ...current };
                delete next[editingValueTag];
                return next;
            });
        }
    }, [visibleItems, editingValueTag]);
    const successfulUpsells = visibleItems.reduce((total, item) => total + item.count, 0);
    const upsellSuccessRate = upsellAttempts > 0 ? successfulUpsells / upsellAttempts * 100 : 0;
    const totalRevenue = visibleItems.reduce((total, item) => total + item.revenue, 0);
    const averageUpsellRevenue = successfulUpsells > 0 ? totalRevenue / successfulUpsells : 0;
    const averageRevenuePerLocation = selectedLocationCount > 0 ? totalRevenue / selectedLocationCount : 0;
    const topItems = useMemo(() => [...visibleItems].sort((a, b) => b.count - a.count).slice(0, 10), [visibleItems]);
    const temporalItems = useMemo(() => [...visibleItems].sort((a, b) => b.count - a.count).slice(0, 5), [visibleItems]);
    const itemColors = useMemo(() => new Map([...items]
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
        .map((item, index) => [normalize(item.key), ITEM_COLORS[index % ITEM_COLORS.length]])), [items]);
    const itemColor = (key: string) => itemColors.get(normalize(key)) || ITEM_COLORS[0];
    const itemBreakdowns = useMemo(() => new Map(buildTopUpsellItems(
        summaries, hiddenItemKeys, spanishSessionKeys, appNameMap, sessionLanguages, Infinity
    ).map(item => [item.key, item.apps])), [summaries, hiddenItemKeys, spanishSessionKeys, appNameMap, sessionLanguages]);

    const temporalRowsByMode = useMemo(() => {
        const buildRows = (mode: TemporalMode) => {
            const buckets: Array<string | number> = mode === 'days' ? getUpsellDaysInRange(parseAnalyticsDate(startDate).toDate(), parseAnalyticsDate(endDate).toDate()) : Array.from({ length: 24 }, (_, hour) => hour);
            const attemptsByBucket = mode === 'days' ? upsellAttemptsByDay : upsellAttemptsByHour;
            const rows = buckets.map(bucket => {
                const row: Record<string, string | number> = {
                    bucket: mode === 'days' ? bucket as string : `${(bucket as number) % 12 || 12} ${(bucket as number) < 12 ? 'AM' : 'PM'}`,
                    attempts: attemptsByBucket[String(bucket)] || 0
                };
                temporalItems.forEach(item => { row[`count:${item.name}`] = 0; });
                summaries.forEach(({ applicationId, summary }) => {
                    const addSuccess = (tag: string, count: number) => {
                        const item = temporalItems.find(item => normalize(item.key) === normalize(tag));
                        if (item) row[`count:${item.name}`] = Number(row[`count:${item.name}`]) + count;
                    };
                    if (spanishSessionKeys) {
                        Object.entries(summary.withSessionId || {}).forEach(([tag, conversations]) => conversations.forEach(conversation => {
                            if (conversation.applicationId !== applicationId || !spanishSessionKeys.has(`${applicationId}|${conversation.sessionId}`)) return;
                            const date = new Date(conversation.callTime);
                            if (!Number.isFinite(date.getTime())) return;
                            const conversationBucket = mode === 'days' ? UPSELL_DAYS[(date.getDay() + 6) % 7] : date.getHours();
                            if (conversationBucket === bucket) addSuccess(tag, 1);
                        }));
                    } else {
                        const byBucket = mode === 'days' ? summary.byDayOfWeek : summary.byHourOfDay;
                        (byBucket?.[String(bucket)] || []).forEach(({ tag, count }) => addSuccess(tag, count || 0));
                    }
                });
                temporalItems.forEach(item => {
                    row[item.name] = Number(row[`count:${item.name}`]);
                });
                return row;
            });
            return rows;
        };
        return { days: buildRows('days'), hours: buildRows('hours') };
    }, [summaries, temporalItems, upsellAttemptsByDay, upsellAttemptsByHour, spanishSessionKeys, startDate, endDate]);
    const hasRateData = (mode: TemporalMode) => {
        const rows = temporalRowsByMode[mode];
        const hasAttempts = rows.some(row => Number(row.attempts) > 0);
        const hasMatchingAttempts = rows.every(row => temporalItems.every(item => Number(row[item.name]) <= Number(row.attempts)));
        const hasCompleteHourlyCounts = mode !== 'hours' || temporalItems.every(item =>
            rows.reduce((total, row) => total + Number(row[item.name]), 0) === item.count);
        return hasAttempts && hasMatchingAttempts && hasCompleteHourlyCounts;
    };
    const hourlyRatesAvailable = hasRateData('hours');
    const dailyRatesAvailable = hasRateData('days');
    const activeTemporalMode = temporalMetric === 'rates' && !hasRateData(temporalMode)
        ? (dailyRatesAvailable ? 'days' : hourlyRatesAvailable ? 'hours' : temporalMode) : temporalMode;
    const temporalData = useMemo(() => temporalRowsByMode[activeTemporalMode].map(row => {
        const result = { ...row };
        temporalItems.forEach(item => {
            result[item.name] = temporalMetric === 'counts' ? Number(row[item.name])
                : Number(row.attempts) > 0 ? Number(row[item.name]) / Number(row.attempts) * 100 : 0;
        });
        return result;
    }), [temporalRowsByMode, activeTemporalMode, temporalMetric, temporalItems]);
    const hasTemporalData = temporalMetric === 'counts'
        ? temporalData.some(row => Number(row.attempts) > 0 || temporalItems.some(item => Number(row[item.name]) > 0))
        : hasRateData(activeTemporalMode);

    useEffect(() => {
        if (activeTemporalMode !== 'hours' || isLoading || !hasTemporalData) return;
        const viewport = timingScrollRef.current;
        if (!viewport) return;
        let observer: { observe(target: Element): void; disconnect(): void } | undefined;
        const ResizeObserverClass = typeof window !== 'undefined' ? (window as any).ResizeObserver : undefined;
        const centerCurrentHour = () => {
            const plot = viewport.firstElementChild as HTMLElement | null;
            if (!viewport.clientWidth || !plot?.clientWidth) return false;
            // Match the chart's left/right margins and center the current hour's bucket.
            const hourCenter = 72 + (plot.clientWidth - 92) * (new Date().getHours() + .5) / 24;
            viewport.scrollLeft = Math.max(0, Math.min(plot.clientWidth - viewport.clientWidth, hourCenter - viewport.clientWidth / 2));
            observer?.disconnect();
            return true;
        };
        if (!centerCurrentHour() && ResizeObserverClass) {
            observer = new ResizeObserverClass(centerCurrentHour);
            observer.observe(viewport);
        }
        return () => observer?.disconnect();
    }, [activeTemporalMode, isLoading, hasTemporalData]);

    const locationRateData = useMemo(() => locationSuccessRates
        .filter(location => location.rate !== null)
        .map(location => ({ ...location, rate: location.rate as number })), [locationSuccessRates]);

    const locationColors = useMemo(() => new Map([...locationRateData]
        .sort((a, b) => a.applicationId.localeCompare(b.applicationId))
        .map((location, index) => [location.applicationId, ITEM_COLORS[index % ITEM_COLORS.length]])), [locationRateData]);
    const locationColor = (applicationId: string) => locationColors.get(applicationId) || ITEM_COLORS[0];
    const hoveredLocation = locationRateData.find(location => location.applicationId === hoveredLocationId);
    const renderLocationTooltip = (location: UpsellLocationRate) => <ChartTooltip title={location.location} value={formatRate(Number(location.rate))}
        valueLabel="success rate" subtitle={`${location.successes} successful upsells from ${location.attempts} attempts at this location`}
        color={locationColor(location.applicationId)} showApps={false} />;

    const selectedSessions = useMemo(() => {
        if (!selectedItemTag || hiddenItemKeys.has(normalize(selectedItemTag))) return [];
        const sessions: UpsellSession[] = [];
        summaries.forEach(({ summary }) => {
            const matchingSessions = Object.entries(summary.withSessionId || {})
                .find(([tag]) => normalize(tag) === normalize(selectedItemTag))?.[1] || [];
            matchingSessions.forEach(session => {
                if (session.sessionId) sessions.push(session);
            });
        });
        const uniqueSessions = Array.from(new Map(sessions.map(session =>
            [`${session.applicationId}|${session.sessionId}`, session]
        )).values());
        return uniqueSessions.sort((a, b) => {
            const direction = oldestFirst ? 1 : -1;
            return direction * (new Date(a.callTime).getTime() - new Date(b.callTime).getTime());
        });
    }, [summaries, selectedItemTag, oldestFirst, hiddenItemKeys]);

    const openSessions = (itemName: string, itemTag?: string) => {
        setSelectedItem(itemName);
        setSelectedItemTag(itemTag || items.find(item => item.name === itemName)?.key || itemName);
        setOldestFirst(false);
        setPage(0);
    };
    const pageCount = Math.max(1, Math.ceil(selectedSessions.length / PAGE_SIZE));
    const visibleSessions = selectedSessions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    const temporalTicks = countTicks(Math.max(0, ...temporalData.flatMap(row => temporalItems.map(item => Number(row[item.name] || 0)))));
    const locationRateTicks = countTicks(Math.max(20, ...locationRateData.map(location => location.rate)));
    const topItemTicks = countTicks(Math.max(0, ...topItems.map(item => item.count)));
    const axisBottom = {
        tickSize: 0,
        tickPadding: 10,
        tickRotation: activeTemporalMode === 'hours' ? -35 : 0,
        renderTick: tick => <g transform={`translate(${tick.x},${tick.y + 108})`}>
            <text textAnchor="middle" dominantBaseline="middle"
                style={{ fontSize: 11, fill: chart_blue_2, fontWeight: 500 }}>{tick.value}</text>
        </g>
    };

    return (
        <Page>
            <HeaderLayout>
                <PageHeader>
                    <PageTitle>Upsells</PageTitle>
                    <HeaderActions>
                        <BackButton onClick={onBack} type="button"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m12 5-7 7 7 7M5 12h14" /></svg> Back to Dashboard</BackButton>
                        <FilterButton type="button" aria-haspopup="dialog" aria-expanded={isFilterOpen} onClick={() => { setDraftIncludedItemKeys(new Set(visibleItems.map(item => normalize(item.key)))); setFilterSearch(''); setIsFilterOpen(true); }}>
                            <FilterIcon viewBox="0 0 24 24" isActive={excludedItemKeys.size > 0} aria-hidden="true">
                                <path d="M3 4h18v2H3V4zm3 7h12v2H6v-2zm3 7h6v2H9v-2z" />
                            </FilterIcon>
                            Filter Items
                        </FilterButton>
                    </HeaderActions>
                </PageHeader>

                {hasItemFilter && <ActiveFilters aria-label="Active item filters">
                    <ActiveFilterItems>
                        <FilterSummary>included items</FilterSummary>
                        {visibleItems.length ? visibleItems.map(item => <ItemFilterPill key={item.key}>
                            <span>{item.name}</span>
                            <RemoveItemFilter type="button" aria-label={`Remove ${item.name} from included items`} onClick={() => setExcludedItemKeys(current => visibleItems.length === 1 ? new Set() : new Set([...Array.from(current), normalize(item.key)]))}>
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><path d="m4 4 8 8M12 4l-8 8" /></svg>
                            </RemoveItemFilter>
                        </ItemFilterPill>) : <FilterSummary>no items selected</FilterSummary>}
                    </ActiveFilterItems>
                    <ClearFiltersButton type="button" onClick={() => setExcludedItemKeys(new Set())}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 10a8 8 0 1 1 2 8M3 4v6h6" /></svg>Clear all filters
                    </ClearFiltersButton>
                </ActiveFilters>}
            </HeaderLayout>

            <ContentGrid>
                <OverviewGrid>
                    <ConversionColumn>
                        <Card>
                            {hasItemFilter ? <>
                                <TileHeader style={{ justifyContent: 'space-between' }}>
                                    <TileTitle>Successful Upsells</TileTitle>
                                    <MetricInfoTooltip metricKey="FILTERED_UPSELL_COUNT" iconSize={12} customDefinition={{
                                        name: 'Successful Upsells',
                                        description: 'Counts successful upsells for the included items. Attempts are recorded at the conversation level, so an item-specific conversion rate is unavailable.',
                                        calculation: ''
                                    }} />
                                </TileHeader>
                                <FilteredUpsellCount role="status" aria-label="Successful upsells for included items">
                                    {isLoading ? <LoadingBar style={{ height: 34, width: '40%' }} /> : <>
                                        <KpiValue $characters={successfulUpsells.toLocaleString().length}>{successfulUpsells.toLocaleString()}</KpiValue>
                                        <KpiFootnote>for included items</KpiFootnote>
                                    </>}
                                </FilteredUpsellCount>
                            </> : <>
                            <TileHeader style={{ justifyContent: 'space-between' }}>
                                <TileTitle>Upsell Success Rate by Location</TileTitle>
                                {!isLoading && <OverviewStatsSection>
                                    <TileHeaderNumber>{Math.ceil(upsellSuccessRate)}%</TileHeaderNumber>
                                    <TileHeaderLabel>Overall Success Rate <MetricInfoTooltip metricKey="UPSELL_SUCCESS_RATE" iconSize={12} customDefinition={{
                                        name: 'Upsell Success Rate', description: "The overall figure uses successes and attempts across selected locations. Each bar uses only that location's successes divided by its own attempts, for the selected date range and language. Locations with no attempts are excluded.", calculation: ''
                                    }} /></TileHeaderLabel>
                                </OverviewStatsSection>}
                            </TileHeader>
                            <LocationRateChartArea>
                                {isLoading ? <ChartLoading><LoadingBar style={{ width: '80%' }} /><LoadingBar style={{ width: '60%' }} /></ChartLoading>
                                    : locationRateData.length ? <GroupedChartLayout>
                                        <GroupedChartPlot $minWidth={locationRateData.length * 120}>
                                            <ResponsiveBar
                                                data={locationRateData}
                                                keys={['rate']}
                                                indexBy="applicationId"
                                                layout="vertical"
                                                margin={{ top: 22, right: 12, bottom: 28, left: 42 }}
                                                padding={0.55}
                                                valueScale={{ type: 'linear', min: 0, max: locationRateTicks[locationRateTicks.length - 1] }}
                                                colors={({ data }) => locationColor(String(data.applicationId))}
                                                borderRadius={4}
                                                axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: locationRateTicks, format: value => formatRate(Number(value)) }}
                                                axisBottom={{ tickSize: 0, tickPadding: 10, renderTick: tick => {
                                                    const name = locationRateData.find(location => location.applicationId === tick.value)?.location || String(tick.value);
                                                    return <g transform={`translate(${tick.x},${tick.y + 8})`}>
                                                        <text textAnchor="middle" dominantBaseline="middle" aria-label={name} style={{ fontSize: 10, fill: chart_blue_2, cursor: 'help' }}
                                                            onMouseEnter={() => setHoveredLocationId(String(tick.value))}
                                                            onMouseMove={() => setHoveredLocationId(String(tick.value))}
                                                            onMouseLeave={() => setHoveredLocationId(null)}>
                                                            {name.length > 16 ? `${name.slice(0, 13)}...` : name}
                                                        </text>
                                                    </g>;
                                                } }}
                                                enableGridX={false}
                                                gridYValues={locationRateTicks}
                                                enableLabel={false}
                                                layers={['grid', 'axes', 'bars', LocationRateLabels]}
                                                theme={nivoChartTheme}
                                                animate={barChartDefaults.animate}
                                                motionConfig={barChartDefaults.motionConfig}
                                                tooltip={({ data }) => {
                                                    const location = locationRateData.find(location => location.applicationId === data.applicationId);
                                                    return location ? renderLocationTooltip(location) : null;
                                                }}
                                                role="application"
                                                ariaLabel="Upsell success rate by location"
                                            />
                                        </GroupedChartPlot>
                                    </GroupedChartLayout> : <EmptyState>No location-level upsell attempts are available for this date range.</EmptyState>}
                                {hoveredLocation && renderLocationTooltip(hoveredLocation)}
                            </LocationRateChartArea>
                            </>}

                        </Card>
                        <KpiGrid>
                            <KpiCard>
                                <KpiLabel small $singleLine>
                                    Revenue
                                    <MetricInfoTooltip
                                        metricKey="UPSELL_REVENUE_GENERATED_ORDERING"
                                        iconSize={12}
                                        customDefinition={{
                                            name: 'Revenue',
                                            description: 'Includes actual and estimated item values set in the table below.',
                                            calculation: 'quantity sold multiplied by the estimated value for each upsold item.'
                                        }}
                                    />
                                </KpiLabel>
                                {isLoading ? <LoadingBar style={{ height: 34, width: '62%' }} /> : <KpiValue $characters={formatCurrency(totalRevenue).length}>{formatCurrency(totalRevenue)}</KpiValue>}
                            </KpiCard>
                            <KpiCard role="group" aria-labelledby="average-revenue-per-upsell-title">
                                <KpiLabel small id="average-revenue-per-upsell-title">Average Revenue <br /><KpiLabelLine>Per Upsell</KpiLabelLine></KpiLabel>
                                {isLoading ? <LoadingBar style={{ height: 34, width: '62%' }} /> : <KpiValue $compact $characters={formatCurrency(averageUpsellRevenue).length}>{formatCurrency(averageUpsellRevenue)}</KpiValue>}
                            </KpiCard>
                            <KpiCard>
                                <KpiLabel small>Average Revenue <br /><KpiLabelLine>Per Location</KpiLabelLine></KpiLabel>
                                {isLoading ? <LoadingBar style={{ height: 34, width: '62%' }} /> : <KpiValue $compact $characters={formatCurrency(averageRevenuePerLocation).length}>{formatCurrency(averageRevenuePerLocation)}</KpiValue>}
                                {isLoading ? <LoadingBar style={{ height: 13, width: '72%' }} /> : <KpiFootnote>across {selectedLocationCount.toLocaleString()} selected {selectedLocationCount === 1 ? 'location' : 'locations'}</KpiFootnote>}
                            </KpiCard>
                        </KpiGrid>
                    </ConversionColumn>

                    <Card>
                        <TopItemsHeader>
                            <CardTitle>
                                Upsold Items
                                <MetricInfoTooltip metricKey="TOP_UPSOLD_ITEMS" iconSize={12} customDefinition={{
                                    name: 'Upsold Items',
                                    description: 'top 10 by successful upsells',
                                    calculation: ''
                                }} />
                            </CardTitle>
                            <OverviewStatsSection>
                                {isLoading ? <LoadingBar style={{ height: 24, width: 48 }} /> : <>
                                    <TileHeaderNumber>{successfulUpsells.toLocaleString()}</TileHeaderNumber>
                                    <TileHeaderLabel>successful upsells</TileHeaderLabel>
                                </>}
                            </OverviewStatsSection>
                        </TopItemsHeader>
                        <TopItemsChart>
                            {isLoading ? <ChartLoading><LoadingBar style={{ width: '82%' }} /><LoadingBar style={{ width: '68%' }} /><LoadingBar style={{ width: '92%' }} /><LoadingBar style={{ width: '57%' }} /><LoadingBar style={{ width: '73%' }} /></ChartLoading> : topItems.length ? <ResponsiveBar
                                data={[...topItems].reverse().map(item => ({ item: item.name, itemKey: item.key, count: item.count }))}
                                keys={['count']}
                                indexBy="item"
                                layout="horizontal"
                                margin={{ top: 6, right: 6, bottom: 20, left: 112 }}
                                padding={barChartDefaults.padding}
                                valueScale={{ type: 'linear', min: 0, max: topItemTicks[topItemTicks.length - 1] }}
                                colors={({ data }) => itemColor(String(data.itemKey))}
                                borderRadius={barChartDefaults.borderRadius}
                                axisBottom={{ tickSize: 0, tickPadding: 8, tickValues: topItemTicks, format: value => Number(value).toLocaleString() }}
                                axisLeft={{ tickSize: 0, tickPadding: 12, renderTick: tick => <g transform={`translate(${tick.x - 12},${tick.y})`}>
                                    <text textAnchor="end" dominantBaseline="middle" style={{ fontSize: 11, fill: chart_blue_2, fontWeight: 500 }}>
                                        <title>{String(tick.value)}</title>{truncateChartLabel(String(tick.value))}
                                    </text>
                                </g> }}
                                enableGridX={false}
                                enableGridY={false}
                                animate={barChartDefaults.animate}
                                motionConfig={barChartDefaults.motionConfig}
                                enableLabel={false}
                                theme={nivoChartTheme}
                                tooltip={({ indexValue, value, color }) => {
                                    const item = topItems.find(item => item.name === String(indexValue));
                                    return <ChartTooltip title={String(indexValue)} value={value} valueLabel="successful upsells"
                                        apps={itemBreakdowns.get(normalize(item?.key || '')) || []} color={color}
                                        showLanguageBreakdown={showLanguageBreakdown} spanishOnly={!!spanishSessionKeys} />;
                                }}
                                onClick={bar => openSessions(String(bar.indexValue), items.find(item => item.name === String(bar.indexValue))?.key)}
                                role="application"
                                ariaLabel="Top ten upsold items by quantity"
                            /> : <EmptyState>No upsold items for this date range.</EmptyState>}
                        </TopItemsChart>
                    </Card>
                </OverviewGrid>

                <Card>
                    <CardHeader>
                        <ItemTableHeading>
                            <CardTitle>Upsold Items</CardTitle>
                            <ItemTableHint role="note">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M9 18h6M10 21h4M8.5 14.5a6 6 0 1 1 7 0c-1 .7-1.5 1.5-1.5 3.5h-4c0-2-.5-2.8-1.5-3.5Z" />
                                </svg>
                                <span>Click on an item to view associated orders</span>
                            </ItemTableHint>
                        </ItemTableHeading>
                    </CardHeader>
                    <ItemTableWrap>
                        <ItemTable>
                            <thead><CallDataRow>
                                <CallDataTh>Item <SortIcon type="button" isActive={sortMode.startsWith('item-')} aria-label="Sort by item name" aria-pressed={sortMode.startsWith('item-')} onClick={() => setSortMode(mode => mode === 'item-asc' ? 'item-desc' : 'item-asc')}>{sortMode.startsWith('item-') ? (sortMode === 'item-asc' ? '↑' : '↓') : '↕'}</SortIcon></CallDataTh>
                                <CallDataTh><ValueHeading>Value
                                    <SortIcon type="button" isActive={sortMode.startsWith('estimate-')} aria-label="Sort by value" aria-pressed={sortMode.startsWith('estimate-')} onClick={() => setSortMode(mode => mode === 'estimate-asc' ? 'estimate-desc' : 'estimate-asc')}>{sortMode.startsWith('estimate-') ? (sortMode === 'estimate-asc' ? '↑' : '↓') : '↕'}</SortIcon></ValueHeading></CallDataTh>
                                <CallDataTh><ValueHeading>Type <MetricInfoTooltip metricKey="UPSELL_VALUE_TYPE" iconSize={12} customDefinition={{
                                    name: 'Value Type', description: 'actual uses a matched menu price. Estimate is an estimated or manually entered value. Sort this column to review estimates first.', calculation: ''
                                }} /><SortIcon type="button" isActive={sortMode.startsWith('type-')} aria-label="Sort by value type" aria-pressed={sortMode.startsWith('type-')}
                                    onClick={() => setSortMode(mode => mode === 'type-estimate' ? 'type-menu' : 'type-estimate')}>{sortMode === 'type-estimate' ? '↓' : sortMode === 'type-menu' ? '↑' : '↕'}</SortIcon></ValueHeading></CallDataTh>
                                <CallDataTh>Quantity Sold <SortIcon type="button" isActive={sortMode.startsWith('count-')} aria-label="Sort by item frequency" aria-pressed={sortMode.startsWith('count-')} onClick={() => setSortMode(mode => mode === 'count-desc' ? 'count-asc' : 'count-desc')}>{sortMode.startsWith('count-') ? (sortMode === 'count-desc' ? '↓' : '↑') : '↕'}</SortIcon></CallDataTh>
                                <CallDataTh>Revenue <SortIcon type="button" isActive={sortMode.startsWith('revenue-')} aria-label="Sort by dollar value" aria-pressed={sortMode.startsWith('revenue-')} onClick={() => setSortMode(mode => mode === 'revenue-desc' ? 'revenue-asc' : 'revenue-desc')}>{sortMode.startsWith('revenue-') ? (sortMode === 'revenue-desc' ? '↓' : '↑') : '↕'}</SortIcon></CallDataTh>
                            </CallDataRow></thead>
                            <tbody>
                                {isLoading ? <CallDataRow><CallDataTd colSpan={5}><LoadingStack>{[0, 1, 2, 3, 4].map(index => <LoadingBar key={index} style={{ width: `${92 - index * 7}%` }} />)}</LoadingStack></CallDataTd></CallDataRow> : visibleItems.length ? visibleItems.map(item => {
                                    const itemKey = normalize(item.key);
                                    const configuredValues = configuredValuesByItem.get(itemKey) || [];
                                    const distinctValues = Array.from(new Set(configuredValues));
                                    const hasMixedValues = distinctValues.length > 1;
                                    const menuPrice = menuPricesByItem[itemKey];
                                    const manualSavedValue = manualSavedValues[itemKey];
                                    const inputValue = getEstimateInputValue(itemKey);
                                    const isEditing = editingValueTag === itemKey;
                                    const isBusy = isLoading || savingValueTag !== null || menuSaveState === 'saving';
                                    const cancelValue = () => {
                                        setEditingValueTag(null);
                                        setValueDrafts(current => {
                                            const next = { ...current };
                                            delete next[itemKey];
                                            return next;
                                        });
                                        setSaveErrors(current => ({ ...current, [itemKey]: '' }));
                                    };
                                    const saveValue = async (event: React.SyntheticEvent) => {
                                        event.stopPropagation();
                                        const parsedValue = Number(inputValue);
                                        if (inputValue.trim() === '' || !Number.isFinite(parsedValue) || parsedValue < 0) {
                                            setSaveErrors(current => ({ ...current, [itemKey]: 'Enter a valid amount of 0 or more.' }));
                                            return;
                                        }
                                        setSavingValueTag(itemKey);
                                        setSaveErrors(current => ({ ...current, [itemKey]: '' }));
                                        try {
                                            await onSaveItemValue(item.key, parsedValue);
                                            setEditingValueTag(null);
                                            setManualSavedState(current => ({
                                                scopeKey: valueScopeKey,
                                                values: { ...(current.scopeKey === valueScopeKey ? current.values : {}), [itemKey]: parsedValue }
                                            }));
                                            setValueDrafts(current => {
                                                const next = { ...current };
                                                delete next[itemKey];
                                                return next;
                                            });
                                        } catch (error) {
                                            setSaveErrors(current => ({ ...current, [itemKey]: error instanceof Error ? error.message : 'Could not save this value.' }));
                                        } finally {
                                            setSavingValueTag(null);
                                        }
                                    };
                                    return (
                                    <ItemRow key={item.key} onClick={() => openSessions(item.name, item.key)} tabIndex={0} onKeyDown={event => event.key === 'Enter' && openSessions(item.name, item.key)}>
                                        <CallDataTd><ItemLink>{item.name}</ItemLink></CallDataTd>
                                        <CallDataTd onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
                                            <ValueEditor>
                                                {isEditing ? <>
                                                    <CurrencyInput aria-label={`Value for ${item.name}`} type="number" min="0" step="0.01"
                                                        placeholder={hasMixedValues ? 'Mixed' : 'Amount'} value={inputValue} autoFocus disabled={isBusy}
                                                        onChange={event => setValueDrafts(current => ({ ...current, [itemKey]: event.target.value }))}
                                                        onKeyDown={event => {
                                                            event.stopPropagation();
                                                            if (isBusy) return;
                                                            if (event.key === 'Enter') saveValue(event);
                                                            if (event.key === 'Escape') cancelValue();
                                                        }} />
                                                    <SaveValueButton type="button" disabled={isBusy} onClick={saveValue}>
                                                        {savingValueTag === itemKey ? 'Saving…' : 'Save'}
                                                    </SaveValueButton>
                                                    <CancelValueButton type="button" disabled={isBusy} onClick={cancelValue}>Cancel</CancelValueButton>
                                                </> : <>
                                                    <ValueAmount aria-label={`Value for ${item.name}`} data-value={inputValue}>
                                                        {inputValue === '' ? hasMixedValues ? 'Mixed' : '—' : formatCurrency(Number(inputValue))}
                                                    </ValueAmount>
                                                    <SaveValueButton type="button" aria-label={`Edit value for ${item.name}`}
                                                        disabled={isBusy || editingValueTag !== null} onClick={() => {
                                                            setValueDrafts(current => ({ ...current, [itemKey]: inputValue }));
                                                            setSaveErrors(current => ({ ...current, [itemKey]: '' }));
                                                            setEditingValueTag(itemKey);
                                                        }}>Edit</SaveValueButton>
                                                    {menuPrice !== undefined && manualSavedValue === undefined && menuSaveState === 'saving' && <ValueNote role="status">Saving…</ValueNote>}
                                                    {menuPrice !== undefined && manualSavedValue === undefined && menuSaveState === 'error' && <SaveValueButton type="button" disabled={editingValueTag !== null}
                                                        onClick={() => setMenuSaveRetry(current => current + 1)}>Retry</SaveValueButton>}
                                                </>}
                                            </ValueEditor>
                                            {saveErrors[itemKey] && <SaveError role="alert">{saveErrors[itemKey]}</SaveError>}
                                            {menuPrice !== undefined && manualSavedValue === undefined && menuSaveError && <SaveError role="alert">{menuSaveError}</SaveError>}
                                        </CallDataTd>
                                        <CallDataTd><ValueTypeBadge $menu={isMenuValue(itemKey)}>{isMenuValue(itemKey) ? 'Actual' : 'Estimate'}</ValueTypeBadge></CallDataTd>
                                        <CallDataTd>{item.count.toLocaleString()}</CallDataTd>
                                        <CallDataTd>{formatCurrency(item.revenue)}</CallDataTd>
                                    </ItemRow>
                                    );
                                }) : <CallDataRow><CallDataTd colSpan={5}><EmptyState>{items.length ? 'No items selected. Use Filter Items to include items.' : 'No upsold items were returned for this date range.'}</EmptyState></CallDataTd></CallDataRow>}
                            </tbody>
                        </ItemTable>
                    </ItemTableWrap>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Upsell Timing <MetricInfoTooltip metricKey="UPSELL_TIMING_RATE" iconSize={12} customDefinition={temporalMetric === 'rates' ? {
                            name: 'Share of All Upsell Attempts', description: 'Successful upsells for an item divided by all upsell attempts during the same day or hour. This is not the conversion rate for offers of that specific item.', calculation: ''
                        } : {
                            name: 'Item Counts', description: 'Number of successful upsells for each item during the selected day or hour.', calculation: ''
                        }} /></CardTitle>
                        <TimingControls>
                            <SegmentControl role="group" aria-label="Choose timing metric">
                                <SegmentButton type="button" aria-pressed={temporalMetric === 'counts'} $active={temporalMetric === 'counts'} onClick={() => setTemporalMetric('counts')}>Item Counts</SegmentButton>
                                <SegmentButton type="button" aria-pressed={temporalMetric === 'rates'} $active={temporalMetric === 'rates'} disabled={!dailyRatesAvailable && !hourlyRatesAvailable}
                                    title={!dailyRatesAvailable && !hourlyRatesAvailable ? 'Rates require matching upsell attempt data.' : 'Item successes as a share of all upsell attempts'}
                                    onClick={() => setTemporalMetric('rates')}>Rates</SegmentButton>
                            </SegmentControl>
                            <SegmentControl role="group" aria-label="Choose temporal breakdown">
                                {(temporalMetric === 'counts' || dailyRatesAvailable) && <SegmentButton type="button" aria-pressed={activeTemporalMode === 'days'} $active={activeTemporalMode === 'days'} onClick={() => setTemporalMode('days')}>Days Of Week</SegmentButton>}
                                {(temporalMetric === 'counts' || hourlyRatesAvailable) && <SegmentButton type="button" aria-pressed={activeTemporalMode === 'hours'} $active={activeTemporalMode === 'hours'} onClick={() => setTemporalMode('hours')}>Hours Of Day</SegmentButton>}
                            </SegmentControl>
                        </TimingControls>
                    </CardHeader>
                    <ChartArea>
                        {isLoading ? <ChartLoading><LoadingBar style={{ width: '74%' }} /><LoadingBar style={{ width: '88%' }} /><LoadingBar style={{ width: '62%' }} /><LoadingBar style={{ width: '81%' }} /></ChartLoading> : temporalItems.length && hasTemporalData ? <GroupedChartLayout ref={timingScrollRef} tabIndex={0} aria-label="Upsell timing chart">
                            <GroupedChartPlot $minWidth={temporalData.length * temporalItems.length * 28}>
                                <ResponsiveBar
                                    data={temporalData}
                                    keys={temporalItems.map(item => item.name)}
                                    indexBy="bucket"
                                    groupMode="grouped"
                                    margin={{ top: 20, right: 20, bottom: 124, left: 72 }}
                                    padding={barChartDefaults.padding}
                                    innerPadding={2}
                                    valueScale={{ type: 'linear', min: 0, max: temporalTicks[temporalTicks.length - 1] }}
                                    colors={({ id }) => itemColor(temporalItems.find(item => item.name === String(id))?.key || '')}
                                    borderRadius={barChartDefaults.borderRadius}
                                    axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: temporalTicks, format: value => temporalMetric === 'rates' ? formatRate(Number(value)) : Number(value).toLocaleString() }}
                                    axisBottom={axisBottom}
                                    enableGridX={false}
                                    enableGridY={true}
                                    gridYValues={temporalTicks}
                                    animate={barChartDefaults.animate}
                                    motionConfig={barChartDefaults.motionConfig}
                                    tooltip={({ id, indexValue, value, color, data }) => <ChartTooltip title={String(id)}
                                        subtitle={temporalMetric === 'rates' ? `${indexValue} · ${data[`count:${id}`]} successful upsells from ${data.attempts} upsell attempts. Includes attempts for other items, which can lower this rate.` : String(indexValue)}
                                        value={temporalMetric === 'rates' ? formatRate(Number(value)) : Number(value).toLocaleString()} valueLabel={temporalMetric === 'rates' ? 'of all upsell attempts' : 'successful upsells'} showApps={false} color={color} />}
                                    layers={['grid', 'axes', 'bars', TimingItemLabels, 'markers', 'legends', 'annotations']}
                                    enableLabel={false}
                                    theme={nivoChartTheme}
                                    onClick={bar => openSessions(String(bar.id), items.find(item => item.name === String(bar.id))?.key)}
                                    role="application"
                                    ariaLabel="Grouped chart of upsold items by time"
                                />
                            </GroupedChartPlot>
                        </GroupedChartLayout> : <EmptyState>{temporalMetric === 'rates' ? 'No matching upsell attempt data is available for this view.' : 'No successful upsells are available for this view.'}</EmptyState>}
                    </ChartArea>
                </Card>
            </ContentGrid>

            {isFilterOpen && <ModalOverlay onClick={() => setIsFilterOpen(false)}>
                <FilterModal role="dialog" aria-modal="true" aria-labelledby="filter-modal-title" onClick={event => event.stopPropagation()}>
                    <FilterModalHeader>
                        <div>
                            <CardTitle id="filter-modal-title">Filter Upsold Items</CardTitle>
                            <CardSubtitle>select items to include in the metrics, table and charts.</CardSubtitle>
                        </div>
                        <FilterCloseButton type="button" aria-label="Close filters" onClick={() => setIsFilterOpen(false)}>×</FilterCloseButton>
                    </FilterModalHeader>
                    <FilterSearch type="search" aria-label="Search items" placeholder="Search items..." value={filterSearch} onChange={event => setFilterSearch(event.target.value)} />
                    <SelectionActions>
                        <span aria-live="polite">{items.filter(item => draftIncludedItemKeys.has(normalize(item.key))).length} of {items.length} selected</span>
                        <PageButtons>
                            <ClearFilterLink type="button" onClick={() => setDraftIncludedItemKeys(new Set(items.map(item => normalize(item.key))))}>Select all</ClearFilterLink>
                            <ClearFilterLink type="button" onClick={() => setDraftIncludedItemKeys(new Set())}>Deselect all</ClearFilterLink>
                        </PageButtons>
                    </SelectionActions>
                    <FilterList>
                        {filterItems.length ? filterItems.map(item => {
                            const itemKey = normalize(item.key);
                            return (
                                <FilterOption key={item.key} $selected={draftIncludedItemKeys.has(itemKey)}>
                                    <AnalyticsFilterCheckbox
                                        type="checkbox"
                                        checked={draftIncludedItemKeys.has(itemKey)}
                                        onChange={() => setDraftIncludedItemKeys(current => {
                                            const next = new Set(current);
                                            if (next.has(itemKey)) next.delete(itemKey);
                                            else next.add(itemKey);
                                            return next;
                                        })}
                                    />
                                    <span>{item.name}</span>
                                    <FilterOptionCount>{item.count.toLocaleString()}</FilterOptionCount>
                                </FilterOption>
                            );
                        }) : <EmptyState>No items match your search.</EmptyState>}
                    </FilterList>
                    <FilterFooter>
                        <PageButtons>
                            <CancelFilterButton type="button" onClick={() => setIsFilterOpen(false)}>Cancel</CancelFilterButton>
                            <ApplyFilterButton type="button" onClick={() => { setExcludedItemKeys(new Set(items.filter(item => !draftIncludedItemKeys.has(normalize(item.key))).map(item => normalize(item.key)))); setIsFilterOpen(false); }}>Apply filters</ApplyFilterButton>
                        </PageButtons>
                    </FilterFooter>
                </FilterModal>
            </ModalOverlay>}

            {selectedItem && <ModalOverlay onClick={() => setSelectedItem(null)}>
                <SessionModal role="dialog" aria-modal="true" aria-labelledby="session-modal-title" onClick={event => event.stopPropagation()}>
                    <ModalHeader>
                        <div><CardTitle id="session-modal-title">{selectedItem} Conversations</CardTitle><CardSubtitle>{selectedSessions.length.toLocaleString()} successful upsell conversations</CardSubtitle></div>
                        <CloseButton type="button" aria-label="Close conversations" onClick={() => setSelectedItem(null)}>×</CloseButton>
                    </ModalHeader>
                    <ModalTools>
                        <span>Sorted by {oldestFirst ? 'oldest first' : 'most recent first'}</span>
                        <SortToggle type="button" onClick={() => { setOldestFirst(value => !value); setPage(0); }}>
                            {oldestFirst ? 'Show most recent first' : 'Show oldest first'}
                        </SortToggle>
                    </ModalTools>
                    <SessionTableWrap>
                        <SessionTable>
                            <thead><tr><th>Call time</th><th>Application</th><th>Conversation</th></tr></thead>
                            <tbody>
                                {visibleSessions.map(session => (
                                    <tr key={`${session.applicationId}|${session.sessionId}`}>
                                        <td>{new Date(session.callTime).toLocaleString()}</td>
                                        <td>{(appNameMap && appNameMap[session.applicationId]) || session.applicationId}</td>
                                        <td><SessionLink href={`/v/apps/${encodeURIComponent(session.applicationId)}/analytics/sessions?sessionId=${encodeURIComponent(session.sessionId)}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`} target="_blank" rel="noopener noreferrer">View Transcript</SessionLink></td>
                                    </tr>
                                ))}
                                {!visibleSessions.length && <tr><td colSpan={3}><EmptyState>no conversations are available for this item.</EmptyState></td></tr>}
                            </tbody>
                        </SessionTable>
                    </SessionTableWrap>
                    <ModalFooter>
                        <span>Page {page + 1} of {pageCount}</span>
                        <PageButtons>
                            <PageButton type="button" disabled={page === 0} onClick={() => setPage(value => Math.max(0, value - 1))}>Previous</PageButton>
                            <PageButton type="button" disabled={page + 1 >= pageCount} onClick={() => setPage(value => Math.min(pageCount - 1, value + 1))}>Next</PageButton>
                        </PageButtons>
                    </ModalFooter>
                </SessionModal>
            </ModalOverlay>}
        </Page>
    );
};

const Page = styled.div`
    background: #f4f6f9;
    color: #1e293b;
    min-height: 100%;
    padding: 0 0 24px;
`;
const HeaderLayout = styled.div`display: grid; grid-template-columns: minmax(0, 1fr) auto; column-gap: 20px; row-gap: 16px; margin-bottom: 20px; @media (max-width: 600px) { grid-template-columns: minmax(0, 1fr); }`;
const PageHeader = styled.header`align-items: center; display: grid; grid-column: 1 / -1; grid-template-columns: subgrid; @media (max-width: 600px) { grid-template-columns: minmax(0, 1fr); gap: 16px; }`;
const HeaderActions = styled.div`align-items: center; display: flex; flex-wrap: wrap; gap: 8px;`;
const PageTitle = styled.h1`border-left: 4px solid ${voicify_spark_darker}; color: ${color_shades_darkest}; font-size: 24px; font-weight: 600; letter-spacing: -.6px; line-height: 1.2; margin: 0; padding-left: 12px; ${breakpoint_macbook_small} { font-size: 16px; } ${breakpoint_macbook} { font-size: 20px; } ${breakpoint_large} { font-size: 13px; }`;
const BackButton = styled(ViewDetailsButton)`font: inherit; font-size: 13px; gap: 7px; min-height: 36px; padding: 8px 14px; &:focus-visible { outline: 2px solid ${voicify_spark_darker}; outline-offset: 3px; } svg { flex-shrink: 0; }`;
const FilterButton = styled(BackButton)`background: #fff; border: 1px solid ${color_shades_light}; color: ${voicify_spark_darkest}; &:hover:not(:disabled) { background: ${voicify_spark_lightest}; border-color: ${voicify_spark_darker}; }`;
const FilterIcon = styled(FilterIconSvg)`fill: currentColor; flex-shrink: 0;`;
const ActiveFilters = styled.div`align-items: start; display: grid; grid-column: 1 / -1; grid-template-columns: subgrid; row-gap: 8px;`;
const ActiveFilterItems = styled.div`align-items: center; display: flex; flex-wrap: wrap; gap: 8px; min-width: 0;`;
const FilterSummary = styled.span`color: ${color_text_dashboard_secondary}; font-size: 12px; margin-right: 2px;`;
const ItemFilterPill = styled(CallTag)`
    align-items: center;
    background: ${color_variants_ocean_light_1};
    border-color: ${color_text_link};
    color: ${color_text_link};
    display: inline-flex;
    gap: 4px;
    line-height: 1.2;
    max-width: 100%;
    span { min-width: 0; overflow-wrap: anywhere; }
`;
const RemoveItemFilter = styled(PhoneFilterTagRemove)`
    border-radius: 50%;
    flex-shrink: 0;
    svg { height: 10px; width: 10px; }
    &:hover { background: ${color_variants_ocean_light_1}; }
    &:focus-visible { opacity: 1; outline: 2px solid ${voicify_spark_darker}; outline-offset: 2px; }
`;
const ClearFiltersButton = styled(ClearAllFiltersButton)`
    align-items: center;
    background: #fff;
    border-color: ${voicify_spark_medium};
    border-radius: 6px;
    color: ${color_text_link};
    display: inline-flex;
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 600;
    gap: 6px;
    justify-self: end;
    line-height: 16px;
    min-height: 28px;
    white-space: nowrap;
    padding: 5px 10px;
    &:hover { background: ${voicify_spark_lightest}; border-color: ${voicify_spark_darker}; }
    &:focus-visible { outline: 2px solid ${voicify_spark_darker}; outline-offset: 2px; }
`;
const FilterSearch = styled(PhoneFilterInput)`box-sizing: border-box; flex-shrink: 0; font-family: inherit; height: 34px; line-height: 18px; margin-bottom: 0; padding: 6px 10px;`;
const SelectionActions = styled.div`align-items: center; color: ${color_text_dashboard_secondary}; display: flex; flex-wrap: wrap; font-size: 11px; gap: 8px; justify-content: space-between; padding: 10px 0;`;
const KpiGrid = styled.div`display: grid; min-height: 0; gap: 12px; grid-template-columns: repeat(3, minmax(0, 1fr)); @media (max-width: 600px) { grid-template-columns: 1fr; }`;
const KpiCard = styled(BaseTile)`container-type: inline-size; align-items: center; justify-content: flex-start; min-width: 0; min-height: 0; padding: 16px; text-align: center;`;
const KpiLabel = styled(TileTitle)<{ $singleLine?: boolean }>`flex-shrink: 0; line-height: 1.3; margin: 0; min-height: ${props => props.$singleLine ? '1.3em' : '2.6em'}; width: 100%;`;
const KpiLabelLine = styled.span`white-space: nowrap;`;
const KpiValue = styled(BigNumber)<{ $characters?: number; $compact?: boolean }>`
    font-size: min(clamp(24px, 4dvh, 38px), ${props => 100 / (Math.max(4, props.$characters || 4) * .7)}cqi);
    ${props => props.$compact && `&& { font-size: min(clamp(20px, 2.7dvh, 26px), ${100 / (Math.max(4, props.$characters || 4) * .8)}cqi); letter-spacing: -.75px; }`}
    margin-top: 6px;
`;
const KpiFootnote = styled(NumberSubtext)`max-width: 100%; margin-top: 6px; line-height: 1.3;`;
const ContentGrid = styled.div`display: grid; gap: 16px;`;
const OverviewGrid = styled.div`display: grid; gap: 16px; grid-template-columns: repeat(2, minmax(0, 1fr)); height: clamp(300px, 36dvh, 360px); align-items: stretch; @media (max-width: 1100px) { grid-template-columns: 1fr; height: auto; }`;
const Card = styled(BaseTile.withComponent('section'))`min-width: 0;`;
const FilteredUpsellCount = styled.div`align-items: center; container-type: inline-size; display: flex; flex: 1; flex-direction: column; justify-content: center; min-height: 0; width: 100%;`;
const ConversionColumn = styled.div`display: grid; gap: 12px; grid-template-rows: minmax(0, 1fr) auto; min-height: 0; @media (max-width: 1100px) { grid-template-rows: 200px auto; }`;

const CardHeader = styled.div`align-items: center; display: flex; gap: 14px; justify-content: space-between; margin-bottom: 16px; @media (max-width: 650px) { align-items: flex-start; flex-direction: column; }`;
const OverviewStatsSection = styled(TileHeaderStatsSection)`
    & > div:first-of-type { font-size: clamp(25px, 2.2vw, 32px); }
`;
const TopItemsHeader = styled(TileHeader)`justify-content: space-between; margin-bottom: 4px;`;
const CardTitle = styled(TileTitle.withComponent('h2'))`text-align: left; margin: 0;`;
const CardSubtitle = styled.div`color: ${color_text_dashboard_secondary}; font-size: 12px; margin-top: 5px;`;
const ItemTableHeading = styled.div`align-items: flex-start; display: flex; flex-direction: column; gap: 6px;`;
const ItemTableHint = styled.div`
    align-items: center;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 6px;
    color: #0369a1;
    display: inline-flex;
    font-size: 11px;
    gap: 7px;
    line-height: 1.5;
    padding: 6px 10px;
    svg { color: #0369a1; flex-shrink: 0; }
`;
const ItemTableWrap = styled.div`height: clamp(180px, calc(64dvh - 230px), 360px); max-width: 100%; overflow: auto; @media (max-width: 1100px) { height: 360px; }`;
const ItemTable = styled(CallDataTable)`th:nth-child(n+2), td:nth-child(n+2) { text-align: right; } th:nth-child(2), td:nth-child(2) { width: 240px; } th:nth-child(3), td:nth-child(3) { text-align: left; width: 110px; white-space: nowrap; } th:nth-child(5), td:nth-child(5) { width: 110px; white-space: nowrap; }`;
const ValueHeading = styled.span`align-items: center; display: inline-flex; gap: 8px; white-space: nowrap;`;
const ItemRow = styled(CallDataRow)`cursor: pointer;`;
const ItemLink = styled.span`color: ${voicify_spark_darker}; font-weight: 500; &:hover { color: ${voicify_spark_darkest}; text-decoration: underline; }`;
const EmptyState = styled.div`align-items: center; color: #94a3b8; display: flex; font-size: 13px; justify-content: center; min-height: 120px; padding: 14px; text-align: center;`;
const LoadingBar = styled.div`animation: upsell-loading 1.35s ease-in-out infinite; background: linear-gradient(90deg, #e8edf3 25%, #f5f7fa 45%, #e8edf3 65%); background-size: 300% 100%; border-radius: 6px; height: 14px; @keyframes upsell-loading { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }`;
const LoadingStack = styled.div`display: flex; flex-direction: column; gap: 18px; padding: 22px 8px;`;
const ChartLoading = styled.div`align-content: center; align-items: flex-start; display: flex; flex-direction: column; gap: 24px; height: 100%; justify-content: center; padding: 28px 14px;`;
const TimingControls = styled.div`display: flex; align-items: center; flex-wrap: wrap; gap: 12px;`;
const SegmentControl = styled.div`background: #f1f5f9; border-radius: 9px; display: inline-flex; gap: 3px; padding: 3px;`;
const SegmentButton = styled.button<{ $active: boolean }>`background: ${props => props.$active ? '#fff' : 'transparent'}; border: 0; border-radius: 7px; box-shadow: ${props => props.$active ? '0 1px 3px rgba(15, 23, 42, .12)' : 'none'}; color: ${props => props.$active ? '#0369a1' : '#64748b'}; cursor: pointer; font: inherit; font-size: 11px; font-weight: 600; padding: 8px 10px; white-space: nowrap; &:disabled { cursor: default; opacity: .45; } &:focus-visible { outline: 2px solid ${voicify_spark_darker}; outline-offset: 2px; }`;
const LocationRateChartArea = styled.div`flex: 1; height: 0; min-height: 0; width: 100%;`;
const ChartArea = styled.div`height: 360px; @media (max-width: 650px) { height: 320px; }`;
const GroupedChartLayout = styled.div`height: 100%; min-height: 0; overflow-x: auto; overflow-y: hidden; scrollbar-width: thin;`;
const GroupedChartPlot = styled.div<{ $minWidth: number }>`height: 100%; min-width: ${props => props.$minWidth}px; width: 100%;`;
const TopItemsChart = styled.div`flex: 1 1 auto; height: 0; min-height: 0; @media (max-width: 1100px) { flex: none; height: 280px; }`;
const ModalOverlay = styled.div`align-items: center; background: rgba(15, 23, 42, .52); display: flex; inset: 0; justify-content: center; padding: 20px; position: fixed; z-index: 10000;`;
const FilterModal = styled.div`
    background: #fff;
    border: 1px solid ${color_shades_light};
    border-radius: 12px;
    box-shadow: 0 16px 48px rgba(15, 23, 42, .2);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    font-size: 13px;
    max-height: min(80dvh, 640px);
    max-width: 480px;
    overflow: hidden;
    padding: 20px;
    width: 100%;
`;
const FilterList = styled.div`
    border-top: 1px solid ${color_shades_light};
    flex: 1 1 auto;
    min-height: 72px;
    overflow: auto;
    scrollbar-width: thin;
`;
const FilterOption = styled.label<{ $selected: boolean }>`
    align-items: center;
    background: ${props => props.$selected ? voicify_spark_lightest : 'transparent'};
    border-bottom: 1px solid ${color_shades_light};
    color: ${color_shades_darkest};
    cursor: pointer;
    display: flex;
    font-size: 13px;
    gap: 10px;
    line-height: 18px;
    padding: 8px 10px;
    &:last-child { border-bottom: 0; }
    &:hover { background: ${voicify_spark_lightest}; }
    &:focus-within { box-shadow: inset 0 0 0 1px ${voicify_spark_darker}; }
    input { height: 14px; width: 14px; }
`;
const FilterOptionCount = styled.span`color: ${color_text_dashboard_secondary}; font-size: 11px; margin-left: auto; padding-left: 8px; font-variant-numeric: tabular-nums;`;
const FilterFooter = styled.div`align-items: center; border-top: 1px solid ${color_shades_light}; display: flex; flex: 0 0 auto; justify-content: flex-end; padding-top: 12px;`;
const SessionModal = styled.div`background: #fff; border-radius: 12px; box-shadow: 0 20px 60px rgba(15, 23, 42, .28); display: flex; flex-direction: column; max-height: 90vh; max-width: 980px; overflow: hidden; padding: 20px; width: 100%;`;
const ModalHeader = styled.div`align-items: flex-start; display: flex; justify-content: space-between; margin-bottom: 16px;`;
const CloseButton = styled.button`background: transparent; border: 0; color: #64748b; cursor: pointer; font-size: 26px; line-height: 1;`;
const FilterModalHeader = styled(ModalHeader)`gap: 12px; margin-bottom: 12px;`;
const FilterCloseButton = styled(CloseButton)`align-items: center; border-radius: 6px; display: inline-flex; flex-shrink: 0; font-size: 22px; height: 28px; justify-content: center; padding: 0; width: 28px; &:hover { background: ${voicify_spark_lightest}; } &:focus-visible { outline: 2px solid ${voicify_spark_darker}; }`;

const ModalTools = styled.div`align-items: center; color: #64748b; display: flex; font-size: 12px; justify-content: space-between; margin: 10px 0;`;
const SortToggle = styled.button`background: transparent; border: 0; color: #0369a1; cursor: pointer; font: inherit; font-weight: 600;`;
const SessionTableWrap = styled.div`flex: 1 1 auto; min-height: 0; overflow: auto;`;
const SessionTable = styled.table`border-collapse: collapse; width: 100%; th { background: #f8fafc; color: #64748b; font-size: 11px; padding: 10px; position: sticky; text-align: left; top: 0; z-index: 1; } td { border-top: 1px solid #eef2f6; color: #334155; font-size: 12px; padding: 11px 10px; }`;
const SessionLink = styled.a`color: #0369a1; font-weight: 600; text-decoration: none; &:hover { text-decoration: underline; }`;
const ModalFooter = styled.div`align-items: center; border-top: 1px solid #eef2f6; color: #64748b; display: flex; flex: 0 0 auto; font-size: 12px; justify-content: space-between; margin-top: 12px; padding-top: 12px;`;
const PageButtons = styled.div`display: flex; gap: 8px;`;
const PageButton = styled.button`background: #fff; border: 1px solid #cbd5e1; border-radius: 7px; color: #334155; cursor: pointer; font: inherit; padding: 7px 10px; &:disabled { color: #94a3b8; cursor: default; }`;
const CancelFilterButton = styled(PageButton)`border-color: ${color_shades_light}; border-radius: 6px; font-size: 13px; line-height: 18px; min-height: 32px; padding: 6px 12px; &:hover { background: ${voicify_spark_lightest}; } &:focus-visible { outline: 2px solid ${voicify_spark_darker}; outline-offset: 2px; }`;
const ApplyFilterButton = styled(CancelFilterButton)`background: ${color_text_link}; border-color: ${color_text_link}; color: #fff; font-weight: 600; &:hover { background: ${voicify_spark_darkest}; }`;

export default UpsellAnalyticsDashboard;
